import React, { useEffect, useState } from "react";
import {
  BookOpen, Download, Award, Image as ImageIcon, ClipboardList, Send, Copy, Check,
} from "lucide-react";
import {
  COPY_BLOCKS, PUBLISH_CHECKLIST, RULES_IN_ONE_BREATH,
  LOGOS, TREES, HEADER_MARK, logoSvgUrl, logoPngUrl, badgeUrl,
  GUIDE_DOCX_URL, FOUR_RULES, readRung, fillPlaceholders, buildPlaceholderValues,
} from "../data/brandKit";
import "./BrandMarketing.css";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Cloudinary serves a download rather than a render when fl_attachment is in the path.
const asDownload = (url) => url.replace("/upload/", "/upload/fl_attachment/");

function CopyButton({ text, label = "Copy" }) {
  const [done, setDone] = useState(false);
  return (
    <button
      className="btn btn-text bm-copy"
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setDone(true);
        setTimeout(() => setDone(false), 1600);
      }}
    >
      {done ? <Check size={14} strokeWidth={2} /> : <Copy size={14} strokeWidth={1.8} />}
      {done ? "Copied" : label}
    </button>
  );
}

function AssetTile({ asset }) {
  return (
    <div className="bm-asset">
      {/* A neutral grey tile, so the white variants are visible at all. */}
      <div className="bm-asset-plate">
        <img src={logoSvgUrl(asset)} alt={asset.label} loading="lazy" />
      </div>
      <div className="bm-asset-name">{asset.label}</div>
      <div className="bm-asset-actions">
        <a className="btn btn-text" href={asDownload(logoSvgUrl(asset))}>
          <Download size={13} strokeWidth={1.8} /> SVG
        </a>
        <a className="btn btn-text" href={asDownload(logoPngUrl(asset))}>
          <Download size={13} strokeWidth={1.8} /> PNG
        </a>
      </div>
    </div>
  );
}

export const REVIEW_ADDRESS = "customerservice@henry-griffitts.com";

export default function BrandMarketing({ user, onOpenGuidelines }) {
  // `null` means "not asked yet"; the readRung() call below is what turns a failed request
  // into an explicit unknown rather than into a confident Certified.
  const [eligibility, setEligibility] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // Facility and city come from /api/profile, which is the record that owns them. The `user`
  // prop is the auth identity and is not guaranteed to carry either.
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      // The two reads are independent: a profile that fails to load must not make the rung
      // unreadable, and vice versa. Settled separately for that reason.
      const [elig, prof] = await Promise.allSettled([
        fetch(`${API_URL}/api/education/certification/eligibility`, { credentials: "include" }),
        fetch(`${API_URL}/api/profile`, { credentials: "include" }),
      ]);
      if (!live) return;
      try {
        // CHECK THE STATUS BEFORE READING THE BODY. A non-200 is "I do not know", never
        // "there is no certificate" -- those two lead to different messages on this page.
        if (elig.status !== "fulfilled" || !elig.value.ok) throw new Error("eligibility");
        setEligibility(await elig.value.json());
      } catch (e) {
        if (live) setLoadFailed(true);
      }
      // A profile we could not read leaves the placeholders BRACKETED, which is the same
      // outcome as a field the fitter has not filled in yet, and the honest one either way.
      if (prof.status === "fulfilled" && prof.value.ok) {
        try { setProfile(await prof.value.json()); } catch (e) { /* leave bracketed */ }
      }
      if (live) setLoaded(true);
    })();
    return () => { live = false; };
  }, []);

  const { known, reason, rung } = readRung(eligibility, { failed: loadFailed });

  const fitterName = profile?.full_name || user?.full_name || user?.name || null;
  // Their OWN fields now (Randall, 2026-09-02) -- not `company`, which is a business name and
  // not reliably either of these. Empty stays empty, and the placeholder stays bracketed.
  const facility = profile?.facility || null;
  const city = profile?.city || null;

  const values = buildPlaceholderValues({ name: fitterName, rung, facility, city });

  const reviewSubject = `HG pages for review — ${facility || fitterName || "my pages"}`;
  const reviewBody =
    "Here is the page (or post) carrying the Henry-Griffitts name, for review before it goes live.\n\n" +
    "Staging link: \n\n" +
    "What is on it: \n\n" +
    "When I plan to publish: \n";
  // A ROLE ADDRESS, NOT A PERSON'S (2026-10-02). This used to open mail to an address carrying his name, which a fitter
  // saw in his own mail program. REVIEW_ADDRESS is the company's customer-service mailbox: the one address he has
  // himself given as the current contact point (Appendix E's current contact line). Whoever reads that mailbox has to
  // know these arrive; that is a note for him, not something the app can check.
  const mailto =
    `mailto:${REVIEW_ADDRESS}?subject=${encodeURIComponent(reviewSubject)}` +
    `&body=${encodeURIComponent(reviewBody)}`;

  return (
    <div className="brand-marketing" data-testid="brand-marketing">
      <header className="bm-header">
        {/* THE REVERSED MARK, because this page is dark. The full-colour logo carries a navy
            wordmark that disappears on --bg; using it here would be the page telling fitters
            to use the right variant while using the wrong one itself. Picked by index-free
            lookup so reordering LOGOS cannot silently swap the mark. */}
        <img className="bm-mark" src={logoSvgUrl(HEADER_MARK)} alt="Henry-Griffitts" />
        <h2>Promote Henry-Griffitts fitting the right way</h2>
        <p className="bm-rules-line">{FOUR_RULES}</p>
        <button className="btn btn-text bm-guidelines-link" onClick={onOpenGuidelines}>
          <BookOpen size={14} strokeWidth={1.8} /> Read the brand guidelines
        </button>
      </header>

      <section className="marketing-section">
        <h3><BookOpen size={16} strokeWidth={1.8} className="bm-h3-icon" />Download the guide</h3>
        <p className="section-desc">
          The Co-Marketing Guide and the full Marketing Kit, as a Word document.
        </p>
        <a className="btn btn-primary" href={asDownload(GUIDE_DOCX_URL)}>
          <Download size={15} strokeWidth={1.8} /> Download the guide (.docx)
        </a>
      </section>

      <section className="marketing-section">
        <h3><Award size={16} strokeWidth={1.8} className="bm-h3-icon" />Your badge</h3>
        {!loaded ? (
          <p className="section-desc">Reading your certification…</p>
        ) : (
          <>
            <p className="section-desc">
              You hold <strong>Henry-Griffitts {rung.title}</strong>. This is the badge for your
              rung — use this one and nothing above it.
            </p>
            {!known && (
              <p className="bm-note" data-testid="rung-note">
                {reason === "unreadable"
                  ? "Your certification could not be read just now, so the Certified Fitter badge is shown. Reload before using it, or check with Henry-Griffitts."
                  : "Your rung is set by Henry-Griffitts at certification."}
              </p>
            )}
            <div className="bm-badges">
              {["charcoal", "white"].map((variant) => (
                <div className="bm-badge" key={variant}>
                  <div className={`bm-badge-plate bm-badge-${variant}`}>
                    <img src={badgeUrl(rung.badge, variant)} alt={`${rung.title} — ${variant}`} />
                  </div>
                  <div className="bm-asset-name">{variant === "charcoal" ? "Charcoal" : "White"}</div>
                  <a className="btn btn-text" href={asDownload(badgeUrl(rung.badge, variant))}>
                    <Download size={13} strokeWidth={1.8} /> Download
                  </a>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <section className="marketing-section">
        <h3><ImageIcon size={16} strokeWidth={1.8} className="bm-h3-icon" />Logo files</h3>
        <p className="section-desc">
          The tree with the wordmark is the mark. Don't redraw it, recolour it, or use any
          other version.
        </p>
        <div className="bm-asset-grid">
          {[...LOGOS, ...TREES].map((a) => <AssetTile asset={a} key={a.id} />)}
        </div>
      </section>

      <section className="marketing-section">
        <h3><Copy size={16} strokeWidth={1.8} className="bm-h3-icon" />Copy blocks</h3>
        <p className="section-desc">
          Pre-approved copy from the Marketing Kit, filled in from your record where we hold the
          field. Anything still in [brackets] is yours to write.
        </p>
        <div className="bm-blocks">
          {COPY_BLOCKS.map((block) => {
            const filled = fillPlaceholders(block.text, values);
            return (
              <div className="bm-block" key={block.id} data-testid={`copy-block-${block.id}`}>
                <div className="bm-block-head">
                  <h4>{block.title}</h4>
                  <CopyButton text={filled} />
                </div>
                <pre className="bm-block-text">{filled}</pre>
              </div>
            );
          })}
        </div>
      </section>

      <section className="marketing-section bm-rules">
        <h3><ClipboardList size={16} strokeWidth={1.8} className="bm-h3-icon" />The rules</h3>
        <p className="bm-one-breath">{RULES_IN_ONE_BREATH}</p>
        <h4>Before you publish</h4>
        <ul className="bm-checklist">
          {PUBLISH_CHECKLIST.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      </section>

      <section className="marketing-section">
        <h3><Send size={16} strokeWidth={1.8} className="bm-h3-icon" />Send for review</h3>
        <p className="section-desc">
          Rule four: show us before it goes live. We turn it around in a few working days, and
          we'll link to you from our site when it launches.
        </p>
        <a className="btn btn-primary" href={mailto} data-testid="send-for-review">
          <Send size={15} strokeWidth={1.8} /> Send my pages for review
        </a>
      </section>
    </div>
  );
}
